/** Schematic previews of the actual dashboard charts, without athlete data. */
export type PreviewTone = 'accent' | 'pink' | 'purple' | 'blue' | 'teal' | 'gold' | 'muted' | 'grid';
type Paint = {fill?: PreviewTone; stroke?: PreviewTone; strokeWidth?: number; opacity?: number; strokeDasharray?: string};
export type PreviewShape = Paint & (
  | {kind: 'path'; d: string}
  | {kind: 'rect'; x: number; y: number; width: number; height: number; rx?: number}
  | {kind: 'circle'; cx: number; cy: number; r: number}
);
export type LibraryPreviewKind = 'pmc' | 'annual' | 'curve' | 'powerProfile' | 'running' | 'runningLine' | 'ftp' | 'durability' | 'reserve' | 'bars' | 'zones' | 'donut' | 'readiness' | 'peaks' | 'metrics';
const path = (d: string, stroke: PreviewTone, strokeWidth = 2, extra: Paint = {}): PreviewShape => ({kind: 'path', d, stroke, strokeWidth, ...extra});
const rect = (x: number, y: number, width: number, height: number, fill: PreviewTone, extra: Paint = {}): PreviewShape => ({kind: 'rect', x, y, width, height, rx: 1.5, fill, ...extra});
const dot = (cx: number, cy: number, fill: PreviewTone, r = 2): PreviewShape => ({kind: 'circle', cx, cy, r, fill});
const baseline = () => path('M8 8V56H114', 'grid', .8);
export function chartLibraryPreview(kind: LibraryPreviewKind, metric?: string): PreviewShape[] {
  switch (kind) {
    case 'pmc': return [
      path('M8 40H114 M8 52H114', 'grid', .8),
      path('M8 33C24 30 27 31 40 24S64 26 78 18S99 18 114 12', 'accent'),
      path('M8 24C20 8 26 36 40 17S58 8 70 25S91 9 100 17S110 19 114 15', 'pink'),
      path('M8 51C23 55 26 43 40 47S61 57 76 50S100 43 114 49', 'teal', 1.6),
    ];
    case 'annual': return [baseline(),
      ...[22, 31, 36, 27, 38, 44].flatMap((h, i) => [
        {...rect(12 + i * 17, 56 - h, 10, h, 'blue'), fill: undefined, stroke: 'blue' as const, strokeWidth: 1},
        rect(14 + i * 17, 56 - h * .75, 6, h * .75, 'pink', {opacity: .55}),
      ]),
      path('M10 38L30 32L50 29L70 25L90 19L112 12', 'blue', 1.6, {strokeDasharray: '3 3'}),
      path('M10 40L30 35L50 30L70 31L90 23L112 18', 'accent'),
    ];
    case 'curve': return [baseline(),
      path('M10 14C23 17 26 30 42 36S79 47 113 52', 'pink', 1.5),
      path('M10 9C23 12 27 24 42 30S79 43 113 48L113 55H10Z', 'accent', 0, {fill: 'accent', opacity: .1}),
      path('M10 9C23 12 27 24 42 30S79 43 113 48', 'accent', 2.4), dot(42, 30, 'accent', 2.8),
    ];
    case 'powerProfile': return [
      ...[0, 1, 2, 3, 4, 5, 6, 7].map(i => rect(8, 8 + i * 6, 106, 6, 'purple', {opacity: .04 + i * .02})),
      ...[31, 40, 26, 35].flatMap((h, i) => [rect(15 + i * 25, 56 - h, 7, h, 'accent'), rect(24 + i * 25, 61 - h, 7, h - 5, 'muted', {opacity: .5})]), baseline(),
    ];
    case 'runningLine': return [baseline(),
      path('M10 14L30 20L50 26L70 36L90 43L112 48', 'pink'),
      path('M10 18L30 23L50 28L70 33L90 39L112 45', 'blue', 1.5),
      ...[[10, 14], [30, 20], [50, 26], [70, 36], [90, 43], [112, 48]].map(([x, y]) => dot(x, y, 'pink')),
    ];
    case 'running': return [baseline(), ...[33, 39, 36, 28, 24, 20].flatMap((h, i) => [
      rect(12 + i * 17, 56 - h, 10, h, i < 2 ? 'pink' : i < 4 ? 'purple' : 'teal'), path(`M${11 + i * 17} ${61 - h}h12`, 'muted', 1.8),
    ])];
    case 'ftp': return [
      rect(9, 7, 28, 5, 'muted', {opacity: .25}), rect(46, 6, 28, 7, 'blue', {opacity: .8}), rect(83, 7, 28, 5, 'muted', {opacity: .25}),
      path('M8 37H114', 'muted', 1, {strokeDasharray: '3 3'}), path('M10 47L28 39L47 43L67 28L88 31L112 23', 'blue'),
      ...[[10, 47], [28, 39], [47, 43], [67, 28], [88, 31], [112, 23]].map(([x, y]) => dot(x, y, 'blue')),
    ];
    case 'durability': return [baseline(), path('M10 17L30 18L50 21L70 29L90 35L112 47', 'purple', 2.3),
      ...[[10, 17], [30, 18], [50, 21], [70, 29], [90, 35], [112, 47]].map(([x, y]) => dot(x, y, 'purple')),
    ];
    case 'reserve': return [
      rect(9, 7, 25, 5, 'muted', {opacity: .3}), rect(47, 7, 25, 5, 'pink', {opacity: .7}), rect(85, 7, 25, 5, 'muted', {opacity: .3}),
      path('M9 20H113 M9 56H113', 'grid', .8), path('M10 22L22 23L32 46L45 29L57 23L67 51L81 34L91 23L102 43L113 30', 'pink', 2.2),
    ];
    case 'zones': return [baseline(), ...[[15, 11, 9, 3], [21, 9, 6, 8], [12, 13, 7, 4], [18, 13, 8, 6], [23, 8, 6, 4], [15, 10, 5, 3]].flatMap((stack, i) => {
      let y = 56;
      return stack.map((h, j) => {y -= h; return rect(12 + i * 17, y, 10, h, (['blue', 'teal', 'accent', 'gold'] as const)[j]);});
    })];
    case 'donut': return [
      {kind: 'circle', cx: 35, cy: 32, r: 21, stroke: 'grid', strokeWidth: 8},
      path('M35 11A21 21 0 1 1 15 38', 'accent', 8), path('M15 38A21 21 0 0 1 22 16', 'pink', 8), path('M22 16A21 21 0 0 1 35 11', 'blue', 8),
      ...[0, 1, 2].flatMap(i => [dot(72, 19 + i * 13, (['accent', 'pink', 'blue'] as const)[i], 2.5), rect(80, 17 + i * 13, 28 - i * 5, 4, 'muted', {opacity: .4})]),
    ];
    case 'readiness': return [
      rect(7, 10, 50, 45, 'accent', {opacity: .08}), rect(63, 10, 50, 45, 'accent', {opacity: .08}),
      rect(12, 16, 18, 3, 'muted', {opacity: .5}), rect(68, 16, 18, 3, 'muted', {opacity: .5}),
      path('M12 45L20 40L28 42L36 32L44 35L52 26', 'accent'),
      path('M68 45L76 44L84 38L92 40L100 30L108 26', 'accent'),
    ];
    case 'peaks': return [baseline(), path('M12 44L29 39L48 42L67 26L87 30L111 16', 'accent'),
      ...[[12, 44], [29, 39], [48, 42], [67, 26], [87, 30], [111, 16]].map(([x, y]) => dot(x, y, 'accent', 2.4)),
    ];
    case 'metrics': return [baseline(), path('M12 19L31 25L50 21L70 31L90 29L111 40', 'blue'),
      ...[[12, 19], [31, 25], [50, 21], [70, 31], [90, 29], [111, 40]].map(([x, y]) => dot(x, y, 'blue')),
    ];
    case 'bars': return [baseline(), ...[22, 34, 43, 29, 46, 38].map((h, i) => rect(12 + i * 17, 56 - h, 10, h, metric === 'total_time' ? 'blue' : metric === 'calories' ? 'accent' : 'gold'))];
  }
}
